import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_ROOT } from '@/routes/paths';
import { ArrowLeft } from 'lucide-react';
import { RoleChit, RoleModal } from '@/components/organization';
import { OrganizationOverviewCard } from '@/components/organization/OrganizationOverviewCard';
import { Button } from '@/components/ui/Button';
import {
  Security as RolesIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { availablePermissions, OrgPermission } from '@/constants/organization.constants';
import type { DemoCustomRole } from '@/types/demo.types';
import { useDemoOrgData } from '@/hooks/organization/useDemoOrgData';
import { DEF_JAM_ORG_ID } from '@/mockData/common/demoFilters';
import { notifyDemoAction } from '@/utils/demoNotifications';
import { useOrgRoles } from 'src/hooks/organization';
import { useOrg } from 'src/contexts/OrgContext';
import { useDemoOrg } from 'src/contexts/DemoOrgContext';

interface OrganizationRolesProps {
  isDemo?: boolean;
}

const OrganizationRoles: React.FC<OrganizationRolesProps> = ({ isDemo = false }) => {
  const navigate = useNavigate();
  const { selectedOrgId } = useOrg();
  const demoOrgContext = useDemoOrg();
  const orgId = isDemo ? demoOrgContext?.selectedOrg?.orgId : selectedOrgId;
  const activeOrgId = isDemo ? (orgId ?? DEF_JAM_ORG_ID) : orgId;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<DemoCustomRole | null>(null);

  const {
    customRoles: apiCustomRoles,
    createCustomRole,
    updateCustomRole,
    deleteCustomRole,
  } = useOrgRoles(isDemo ? undefined : activeOrgId);

  const { getCustomRolesByOrgId, getOrganizationById } = useDemoOrgData();
  const demoCustomRoles = getCustomRolesByOrgId(activeOrgId ?? DEF_JAM_ORG_ID);
  const organization = getOrganizationById(activeOrgId ?? DEF_JAM_ORG_ID);
  const customRoles = isDemo
    ? demoCustomRoles
    : apiCustomRoles.map((role) => ({ ...role, memberCount: role.memberCount || 0 }));

  const handleDeleteRole = async (roleId: string, roleName: string) => {
    if (isDemo) {
      notifyDemoAction(`Delete role "${roleName}"`, isDemo);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"? This action cannot be undone.`)) {
      return;
    }

    await deleteCustomRole(roleId);
  };

  const handleRoleSave = async (
    name: string,
    description: string | undefined,
    permissions: OrgPermission[],
    customRoleId?: string,
  ) => {
    if (isDemo) {
      notifyDemoAction(`${customRoleId ? 'Update' : 'Create'} role "${name}"`, isDemo);
      return;
    }

    if (!activeOrgId) {
      return;
    }

    if (customRoleId) {
      await updateCustomRole(customRoleId, name, description, permissions);
    } else {
      await createCustomRole(name, description, permissions);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const getPermissionLabel = (permissionKey: string) =>
    availablePermissions.find((permission) => permission.key === permissionKey)?.label || permissionKey;

  return (
    <>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationManagement(activeOrgId || '') : PATH_ROOT.organization.management)}
            className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h1 className="text-2xl font-semibold text-foreground">Custom Roles</h1>
          <p className="mt-1 text-muted-foreground">Create and manage custom roles with specific permissions</p>
        </div>

        {organization && <OrganizationOverviewCard organization={organization} />}

        <div className="mb-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              {customRoles.length} custom roles • {customRoles.reduce((acc, role) => acc + role.memberCount, 0)} members assigned
            </p>
          </div>
          <Button onClick={() => {
            setEditingRole(null);
            setShowCreateModal(true);
          }} variant="default" size="lg">
            <AddIcon className="mr-2 h-4 w-4" />
            Create Custom Role
          </Button>
        </div>

        <div className="space-y-4">
          {customRoles.map((role) => (
            <RoleChit
              key={role.customRoleId}
              role={role}
              onEdit={(nextRole) => {
                setEditingRole(nextRole);
                setShowCreateModal(true);
              }}
              onDelete={handleDeleteRole}
              onViewMembers={(roleName) => notifyDemoAction(`View members with "${roleName}" role`, isDemo)}
              getPermissionLabel={getPermissionLabel}
              formatDate={formatDate}
            />
          ))}
        </div>

        {customRoles.length === 0 && (
          <div className="px-5 py-16 text-center text-muted-foreground">
            <RolesIcon className="mx-auto mb-4 h-12 w-12 opacity-50 text-muted-foreground" />
            <h3 className="mb-2 text-lg text-muted-foreground">No custom roles yet</h3>
            <p className="mb-6">Create custom roles to give team members specific permissions</p>
            <Button onClick={() => {
              setEditingRole(null);
              setShowCreateModal(true);
            }} variant="default" size="lg">
              <AddIcon className="mr-2 h-4 w-4" />
              Create Your First Role
            </Button>
          </div>
        )}
      </div>

      {showCreateModal && (
        <RoleModal
          isDemo={isDemo}
          orgId={activeOrgId || ''}
          editingRole={editingRole}
          onClose={() => setShowCreateModal(false)}
          onSave={handleRoleSave}
        />
      )}
    </>
  );
};

export default OrganizationRoles;
